import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Alert, Container, Row, Col } from 'react-bootstrap';
import { FaEnvelope, FaCheck, FaTimes } from 'react-icons/fa';
import { API_URL } from '../config';
import '../styles/newsletter.css';

const NewsletterSignup = ({ variant = 'default', title, subtitle }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const hasShownModalRef = useRef(false);

  // Scroll detection for modal variant - show once per session
  useEffect(() => {
    if (variant !== 'modal') return;

    // Check if we've already shown the modal this session
    const sessionShown = sessionStorage.getItem('newsletterModalShown');
    if (sessionShown) {
      hasShownModalRef.current = true;
      return;
    }

    const handleScroll = () => {
      const scrolled = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrolled / docHeight) * 100;

      // Show modal at 30% scroll, only once per session
      if (scrollPercent > 30 && !showModal && !success && !hasShownModalRef.current) {
        setShowModal(true);
        hasShownModalRef.current = true;
        sessionStorage.setItem('newsletterModalShown', 'true');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [variant, showModal, success]);

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (variant !== 'modal') return;

    if (showModal) {
      // Calculate scrollbar width
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Lock body
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      // Prevent scroll on touch devices
      document.addEventListener('touchmove', preventScroll, { passive: false });
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('touchmove', preventScroll);
    }

    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [showModal, variant]);

  // Prevent scroll function for touch devices
  const preventScroll = (e) => {
    if (showModal) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setEmail('');
        // Close modal after 3 seconds on success
        setTimeout(() => {
          setShowModal(false);
          setSuccess(false);
        }, 3000);
      } else {
        setError(data.message || 'Subscription failed. Please try again.');
      }
    } catch (err) {
      setError('Unable to subscribe. Please check your connection and try again.');
      console.error('Newsletter subscription error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Default variant (minimal)
  if (variant === 'default') {
    return (
      <div className="newsletter-inline">
        <div className="newsletter-inline-content">
          <h5 className="mb-2">
            <FaEnvelope className="me-2" />
            Subscribe to Updates
          </h5>
          <p className="small text-muted mb-3">Get contractor tips, product updates, and exclusive offers</p>

          {success ? (
            <Alert variant="success" className="mb-0 small">
              <FaCheck className="me-2" />
              Thanks for subscribing! Check your email for a welcome offer.
            </Alert>
          ) : (
            <Form onSubmit={handleSubmit} className="d-flex gap-2">
              <Form.Control
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="small"
              />
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !email}
                size="sm"
                className="newsletter-btn"
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </Form>
          )}
          {error && <Alert variant="danger" className="mt-2 mb-0 small">{error}</Alert>}
        </div>
      </div>
    );
  }

  // Featured variant (large, prominent)
  if (variant === 'featured') {
    return (
      <section className="newsletter-featured py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 className="mb-3 fw-bold">
                Stay Updated with TR Ventilation
              </h2>
              <p className="lead text-muted">
                {subtitle || 'Get exclusive offers, contractor tips, and product updates delivered to your inbox'}
              </p>
              <ul className="small text-muted" style={{ columns: 2 }}>
                <li>✓ Exclusive trade offers</li>
                <li>✓ Product updates</li>
                <li>✓ Installation guides</li>
                <li>✓ Industry tips</li>
                <li>✓ New products first</li>
                <li>✓ Seasonal promotions</li>
              </ul>
            </Col>
            <Col lg={6}>
              <div className="newsletter-featured-form">
                {success ? (
                  <Alert variant="success">
                    <FaCheck className="me-2" />
                    <strong>Success!</strong> Thanks for subscribing. Check your email for a welcome offer.
                  </Alert>
                ) : (
                  <>
                    <h5 className="mb-3">Subscribe Now</h5>
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={loading}
                          size="lg"
                          className="newsletter-input"
                        />
                      </Form.Group>
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={loading || !email}
                        className="w-100"
                      >
                        {loading ? 'Subscribing...' : 'Get Exclusive Offers'}
                      </Button>
                    </Form>
                    <p className="small text-muted mt-3 mb-0">
                      We respect your privacy. Unsubscribe anytime.
                    </p>
                  </>
                )}
                {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  // Sidebar variant (compact, for sidebars)
  if (variant === 'sidebar') {
    return (
      <div className="newsletter-sidebar card">
        <div className="card-body">
          <h6 className="card-title fw-bold mb-2">
            <FaEnvelope className="me-2" />
            Newsletter
          </h6>
          <p className="small text-muted mb-3">Exclusive offers for contractors</p>

          {success ? (
            <Alert variant="success" className="mb-0 py-2 small">
              <FaCheck className="me-1" />
              Subscribed!
            </Alert>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-2">
                <Form.Control
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  size="sm"
                />
              </Form.Group>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={loading || !email}
                className="w-100"
              >
                {loading ? '...' : 'Subscribe'}
              </Button>
            </Form>
          )}
          {error && <p className="small text-danger mt-2 mb-0">{error}</p>}
        </div>
      </div>
    );
  }

  // Modal variant (popup on scroll)
  if (variant === 'modal') {
    return (
      <>
        {showModal && (
          <div className="newsletter-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="newsletter-modal" onClick={(e) => e.stopPropagation()}>
              <button
                className="newsletter-modal-close"
                onClick={() => setShowModal(false)}
                aria-label="Close newsletter popup"
              >
                <FaTimes />
              </button>

              <div className="newsletter-modal-content">
                <h2 className="newsletter-modal-title">
                  Stay Updated with TR Ventilation
                </h2>
                <p className="newsletter-modal-subtitle">
                  Get exclusive offers, contractor tips, and product updates delivered to your inbox
                </p>

                {success ? (
                  <Alert variant="success" className="newsletter-modal-success">
                    <FaCheck className="me-2" />
                    <strong>Success!</strong> Thanks for subscribing. Check your email for a welcome offer.
                  </Alert>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        size="lg"
                        className="newsletter-modal-input"
                      />
                    </Form.Group>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={loading || !email}
                      className="newsletter-modal-btn w-100"
                    >
                      {loading ? 'Subscribing...' : 'Get Exclusive Offers'}
                    </Button>
                    <p className="newsletter-modal-privacy">
                      We respect your privacy. Unsubscribe anytime.
                    </p>
                  </Form>
                )}
                {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return null;
};

export default NewsletterSignup;
